// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
import * as React from 'react';
import { AlertTriangle } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { IntegrationDeliveryMode } from '@greenlight/shared';

export interface DeliveryModeSelectorProps {
  /** Delivery modes the entry/integration offers (ADR-0026 §5). */
  deliveryModes: readonly IntegrationDeliveryMode[];
  selected: IntegrationDeliveryMode;
  onChange: (mode: IntegrationDeliveryMode) => void;
  /** Shown when a catalogued entry can't be brokered over the HTTP proxy. */
  proxiedUnavailableNote?: boolean;
  /** Why injected delivery is unavailable, when the profile is proxied-only. */
  injectedUnavailableNote?: string;
  proxiedDescription?: string;
  injectedDescription?: string;
}

/**
 * The proxied/injected picker for an HTTP integration or a service-principal
 * connected database — shared between registration and editing (docs/28
 * §/integrations) so both present the same cards and injected-delivery
 * disclosure ([ADR-0039](../../../../docs/decisions/0039-injected-grant-is-disclosure.md)).
 * Other connected databases and system integrations are always proxied and
 * render a static note instead of this selector.
 */
export function DeliveryModeSelector({
  deliveryModes,
  selected,
  onChange,
  proxiedUnavailableNote = false,
  injectedUnavailableNote,
  proxiedDescription = 'App calls GREENLIGHT_PROXY_URL; the proxy swaps the credential.',
  injectedDescription = 'Credential injected as an env var; the app calls upstream directly.',
}: DeliveryModeSelectorProps): React.JSX.Element {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-foreground">Delivery mode</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {deliveryModes.includes('proxied') && (
          <DeliveryOption
            value="proxied"
            selected={selected === 'proxied'}
            title="Proxied (recommended)"
            description={proxiedDescription}
            onSelect={() => onChange('proxied')}
          />
        )}
        {deliveryModes.includes('injected') && (
          <DeliveryOption
            value="injected"
            selected={selected === 'injected'}
            title="Injected"
            description={injectedDescription}
            onSelect={() => onChange('injected')}
          />
        )}
      </div>
      {injectedUnavailableNote !== undefined && (
        <p className="text-[11.5px] text-muted-foreground">{injectedUnavailableNote}</p>
      )}
      {proxiedUnavailableNote && (
        <p className="text-[11.5px] text-muted-foreground">
          Greenlight can&apos;t broker this integration over the HTTP proxy, so it&apos;s delivered
          as an injected credential.
        </p>
      )}
      {selected === 'injected' && (
        <p
          className="flex items-start gap-1.5 rounded-gl border border-border bg-muted/40 p-3 text-[11.5px] leading-relaxed text-foreground"
          data-testid="delivery-injected-disclosure"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Injected means granted agents and developers can obtain this value, including locally.
            Prefer a dedicated least-privilege upstream account — rotation is the only revocation.
          </span>
        </p>
      )}
    </fieldset>
  );
}

function DeliveryOption({
  value,
  selected,
  title,
  description,
  onSelect,
}: {
  value: IntegrationDeliveryMode;
  selected: boolean;
  title: string;
  description: string;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      data-testid={`delivery-${value}`}
      className={cn(
        'flex flex-col rounded-gl border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
      )}
    >
      <span className="text-[13px] font-semibold text-foreground">{title}</span>
      <span className="mt-0.5 text-[11.5px] leading-snug text-muted-foreground">{description}</span>
    </button>
  );
}
